import { getResidentData, listMessages, unreadCount } from '@/apis/apis'
import useDocumentTitle from '@/components/Helmet'
import { useCustomLoader } from '@/components/Loader'
import GlobalAutocomplete from '@/components/material/Autocomplete'
import CustomDrawer from '@/components/material/CustomDrawer'
import MessageCard from '@/components/MessageCard'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { zodResolver } from '@hookform/resolvers/zod'
import { MarkChatUnread } from '@mui/icons-material'
import { Box, Chip, Grid, Stack, TablePagination, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'

const messageSchema = z.object({
    title : z.string().min(1, "Title is required"),
    body: z.string().min(1, "Message is required"),
    toUsers: z.array(z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().email()
    })).min(1, "At least one recipient is required"),
    type: z.enum(["announcement", "alert", "reminder"], {
        required_error: "Type is required"
    }),
});

const messageTypes = [
    { label: 'Announcement', value: 'announcement' },
    { label: 'Alert', value: 'alert' },
    { label: 'Reminder', value: 'reminder' },
]

const Message = () => {
    useDocumentTitle('Message')
    const { toast } = useToast();
    const { user: userData } = useSelector((state: any) => state.auth);
    const loader = useCustomLoader();
    const [openMessage, setOpenMessage] = useState(false);
    const [residents, setResidents] = useState<any[]>([]);
    const [searchResident, setSearchResident] = useState("");
    const [messageData, setMessageData] = useState<any>({
        counts: 0,
        data: []
    });
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [unread, setUnread] = useState(0);
    const [selectMessage, setSelectMessage] = useState<any>(null);

    const { control, formState: { errors }, handleSubmit, reset } = useForm({
        resolver: zodResolver(messageSchema),
        defaultValues: {
            title : '',
            body: '',
            fromUser: '',
            toUsers: [],
            type: 'announcement'
        }
    })

    useEffect(() => {
        const fetchUnRead = async () => {
            const user = userData?.id
            const data = await unreadCount(user);
            setUnread(data);
        }
        fetchUnRead();
    }, [])

    const fetchMessages = async () => {
        try {
            const data = await listMessages({ search, page, limit });
            setMessageData(data);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        } finally {
            loader(false);
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            loader(true);
            fetchMessages();
        }, search ? 600 : 0);
        return () => clearTimeout(timer);
    }, [search])

    const fetchRecident = async () => {
        try {
            const data = await getResidentData({ filters: { search: searchResident }, page: 0, rowsPerPage: 10 });
            setResidents(data.data);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        } finally {
            loader(false);
        }
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            loader(true);
            fetchRecident();
        }, 600);
        return () => clearTimeout(timer);
    }, [searchResident])

    const onSumbit = async (payload: any) => {
        try {
            payload.toUsers = payload.toUsers.map((user: any) => user._id);
            let response;
            if (selectMessage) {
                const { data } = await axiosInstance.put(`/api/messages/${selectMessage}`, payload);
                response = data;
            }
            else {
                const { data } = await axiosInstance.post('/api/messages', payload);
                response = data;
            }
            if (response.success) {
                toast({
                    title: "Success",
                    variant: "success",
                    description: response.message,
                })
                setOpenMessage(false);
                fetchMessages();
                reset();
            }
            else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response.message || "Something went wrong",
                })
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
    }

    const onEdit = async (id: any) => {
        try {
            setOpenMessage(true);
            setSelectMessage(id?._id);
            const { data } = await axiosInstance.get(`/api/messages/${id?._id}`);
            console.log(data)
            reset(data);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
    }

    return (
        <div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography variant="h6">Messages</Typography>
                    <Chip icon={<MarkChatUnread />} label={`Unread: ${unread ?? 0}`} color="primary" size="small" />
                </Stack>
                <Button variant='default' onClick={() => setOpenMessage(true)}>Send Message</Button>
            </Box>
            <Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Search"
                            onChange={(e) => setSearch(e.target.value)}
                            value={search}
                            placeholder="Search by title and message"
                            size='small'
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ mt: 2 }}>
                <MessageCard message={messageData} toast={toast} fetchMessages={fetchMessages} page={page} setPage={setPage} limit={limit} onEdit={onEdit} userData={userData} />
            </Box>

            {
                openMessage && <CustomDrawer
                    onClose={() => setOpenMessage(false)}
                    title={`${selectMessage ? 'Edit' : 'Send'} Message`}
                    handleSubmit={handleSubmit}
                    onSubmit={onSumbit}
                >
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                control={control}
                                name="title"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Title"
                                        error={!!errors.title}
                                        helperText={errors.title ? errors.title.message : ''}
                                        fullWidth
                                        placeholder='Please enter message title'
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                control={control}
                                name="toUsers"
                                render={({ field }) => (
                                    <GlobalAutocomplete
                                        multiple
                                        {...field}
                                        label="Recipients"
                                        options={residents}
                                        value={field.value}
                                        onChange={(value) => field.onChange(value)}
                                        onInputChange={(value) => setSearchResident(value)}
                                        getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                                        error={!!errors.toUsers}
                                        helperText={errors.toUsers ? errors.toUsers.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                control={control}
                                name="type"
                                render={({ field }) => (
                                    <GlobalAutocomplete
                                        {...field}
                                        label="Type"
                                        options={messageTypes}
                                        value={messageTypes.find(opt => opt.value === field.value) || null}
                                        onChange={(newValue: any) => field.onChange(newValue?.value ?? "")}
                                        getOptionLabel={(option) => option.label}
                                        error={!!errors.type}
                                        helperText={errors.type ? errors.type.message : ''}
                                    />
                                )}
                            />

                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                control={control}
                                name="body"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Message"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        error={!!errors.body}
                                        helperText={errors.body ? errors.body.message : ''}
                                        placeholder='Type your message here...'
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </CustomDrawer>
            }
        </div>
    )
}

export default Message